import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Route recording for staff whose route an admin has switched on (Attendance → Settings → Staff). While the person
 * is checked in and the CRM is open, it sends their location every few minutes, and says so on screen the whole
 * time. It stops at check-out. A browser can't record when the CRM is closed. docs/ATTENDANCE.md
 */
export default function RouteTracker() {
  const { user } = useAuth();
  const [on, setOn] = useState(false);
  const every = useRef(5);

  useEffect(() => {
    if (!user) return undefined;
    let timer = null;
    let stopped = false;
    const send = () => {
      if (!("geolocation" in navigator)) return;
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const { data } = await api.post("/attendance/track",
            { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy });
          if (!data.kept && data.reason === "not checked in") setOn(false);
        } catch { setOn(false); }
      }, () => {}, { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 });
    };
    const check = async () => {
      try {
        const [{ data: s }, { data: today }] = await Promise.all([
          api.get("/attendance/settings", { skipCache: true }), api.get("/attendance/today", { skipCache: true })]);
        every.current = s.settings?.track_interval_min || 5;
        const active = !!s.me?.tracking && !!today?.check_in_at && !today?.check_out_at;
        if (stopped) return;
        setOn(active);
        clearInterval(timer);
        if (active) {
          send();
          timer = setInterval(send, every.current * 60 * 1000);
        }
      } catch { setOn(false); }
    };
    check();
    const recheck = setInterval(check, 10 * 60 * 1000);       // picks up check-in / check-out
    window.addEventListener("attendance:changed", check);
    return () => { stopped = true; clearInterval(timer); clearInterval(recheck); window.removeEventListener("attendance:changed", check); };
  }, [user]);

  if (!on) return null;
  return (
    <div className="fixed bottom-20 lg:bottom-4 left-4 z-40 px-3 py-1.5 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] shadow text-xs flex items-center gap-1.5"
         role="status" data-testid="route-recording">
      <MapPin size={13} className="text-[var(--color-primary)]" /> Route recording while you're checked in
    </div>
  );
}
